import { cloneElement, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { useFormContext } from 'react-hook-form';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import DataTable from '../data-table/DataTable';
import { useArrayLoader } from '../data-table/Loaders';
import { useSelectorStyles } from './ListSelectorStyles';
import { COLUMN_TYPES } from '../data-table/DataTableColumns';
import { INTERNAL_ID_FIELD } from './FormConstants';
import NgpFormButton from './NgpFormButton';
import ValidationError from './ValidationError';

const defaultAddButtonText = i18n._(t`Create`);

function makeUnique(list) {
    return _.map(list, (listItem) => ({
        ...listItem,
        [INTERNAL_ID_FIELD]: listItem?.[INTERNAL_ID_FIELD] || _.uniqueId(),
    }));
}

// Items in the "as" renderProp need a ref forwarder, even though we don't use the ref
export default forwardRef(ListFormInternal);

function ListFormInternal(
    {
        gridColumns,
        dbList,
        fieldName,
        addButtonText = defaultAddButtonText,
        showGridToolbar = false,
        emptyGridText,
        setSelectionIsDirty,
        formComponent,
        postConfirmHandler = (response) => response,
        postDeleteHandler = (response) => response,
        selectedListSorter,
        showSearch = false,
        visibleRows,
        showHeaders = true,
    },
    // eslint-disable-next-line no-unused-vars
    _ref
) {
    const classes = useSelectorStyles();
    const { setValue, errors } = useFormContext();
    const lastDbList = useRef(dbList);
    const [selectedList, setSelectedList] = useState(() => makeUnique(dbList));

    // Keep the internal list in sync with prop-driven updates (for example when a parent
    // calls react-hook-form setValue and re-renders with a new dbList array).
    // NOTE: do not use a default value `dbList = []` in the destructure — that creates a
    // brand-new array reference every render when the caller passes undefined, which would
    // make this effect fire forever. Treat empty/undefined as equivalent here instead.
    useEffect(() => {
        if (lastDbList.current === dbList) {
            return;
        }
        const prevEmpty = _.isEmpty(lastDbList.current);
        const nextEmpty = _.isEmpty(dbList);
        lastDbList.current = dbList;
        if (prevEmpty && nextEmpty) {
            return;
        }
        setSelectedList(makeUnique(dbList));
    }, [dbList]);

    const selectedListLoader = useArrayLoader(selectedList, (data) => data);
    const [selectedRecord, setSelectedRecord] = useState(undefined);
    const [showDialog, setShowDialog] = useState(false);

    const updateSelection = (records, { markDirty = true } = {}) => {
        // Update both the table's local source of truth and the RHF field value.
        // Also advance lastDbList so the next dbList-sync effect sees the same
        // reference and skips a redundant reset.
        lastDbList.current = records;
        setSelectedList(records);
        setValue(fieldName, records);

        if (markDirty && _.isFunction(setSelectionIsDirty)) {
            setSelectionIsDirty(true);
        }
    };

    useImperativeHandle(_ref, () => ({
        removeRecords: (predicate) => {
            const nextList = _.reject(selectedList, predicate);
            if (nextList.length !== selectedList.length) {
                // The change has already been persisted on the server (e.g. move scope),
                // so don't mark the form as dirty.
                updateSelection(nextList, { markDirty: false });
            }
        },
        editRecord: (predicate) => {
            const record = _.isFunction(predicate) ? _.find(selectedList, predicate) : predicate;
            if (record) {
                setSelectedRecord(record);
                setShowDialog(true);
            }
        },
    }));

    const removeOne = async (record) => {
        const nextList = _.filter(
            selectedList,
            (listItem) => listItem[INTERNAL_ID_FIELD] !== record[INTERNAL_ID_FIELD]
        );

        const results = await postDeleteHandler(nextList, record);
        updateSelection(results);
    };

    const editOne = (record) => {
        setSelectedRecord(record);
        setShowDialog(true);
    };

    const handleDialogConfirm = async (response) => {
        if (!response[INTERNAL_ID_FIELD]) {
            _.set(response, INTERNAL_ID_FIELD, _.uniqueId());
        }

        const existingIndex = _.findIndex(
            selectedList,
            (listItem) => listItem[INTERNAL_ID_FIELD] === response[INTERNAL_ID_FIELD]
        );

        const combined = [...selectedList];
        if (existingIndex >= 0) {
            combined[existingIndex] = response;
        } else {
            combined.push(response);
        }

        const results = await postConfirmHandler(combined, response);
        updateSelection(results);
        setShowDialog(false);
    };

    const deleteColumnDef = [
        {
            type: COLUMN_TYPES.ICON,
            title: '',
            width: 30,
            icons: [
                {
                    getIcon: () => <DeleteIcon />,
                    getTooltip: () => t`Delete`,
                    clickHandler: removeOne,
                },
            ],
        },
        {
            type: COLUMN_TYPES.ICON,
            title: '',
            width: 30,
            icons: [
                {
                    getIcon: () => <EditIcon />,
                    getTooltip: () => t`Edit`,
                    clickHandler: editOne,
                },
            ],
        },
    ];

    const gridListColumns = _.map([...deleteColumnDef, ...gridColumns], (theColumn) =>
        _.merge(theColumn, { resizable: false })
    );

    return (
        <>
            <div className={classes.formSelectorContainer}>
                <ValidationError
                    invalid={_.get(errors, fieldName, false)}
                    message={_.get(errors, `${fieldName}.message`)}
                />
                <div className={classes.selectorActions}>
                    <NgpFormButton onClick={() => setShowDialog(true)}>{addButtonText}</NgpFormButton>
                </div>
                {!showGridToolbar && <div className={classes.toolbarSpacer} />}
                <DataTable
                    loader={selectedListLoader}
                    columns={gridListColumns}
                    showToolbar={showGridToolbar}
                    emptyText={emptyGridText}
                    sorters={selectedListSorter}
                    showHeaders={showHeaders}
                    disableCSVExport
                    disableColumnRemover
                    showSearch={showSearch}
                    visibleRows={visibleRows}
                />
            </div>

            <>
                {cloneElement(formComponent, {
                    selectedList,
                    selectedRecord,
                    open: showDialog,
                    setOpen: setShowDialog,
                    setSelectedRecord,
                    onConfirm: handleDialogConfirm,
                })}
            </>
        </>
    );
}
